import * as RS from '@radix-ui/react-switch';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
  id,
  small,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  id?: string;
  small?: boolean;
}) {
  return (
    <RS.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'relative shrink-0 rounded-full bg-elevated border border-line transition-colors duration-200 data-[state=checked]:bg-pink data-[state=checked]:border-pink disabled:opacity-50',
        small ? 'h-[23px] w-10' : 'h-7 w-12',
      )}
    >
      <RS.Thumb
        className={cn(
          'block translate-x-0.5 rounded-full shadow transition-transform duration-200 ease-[var(--ease-out-soft)] data-[state=checked]:bg-on-pink',
          small
            ? 'h-[17px] w-[17px] bg-muted data-[state=checked]:translate-x-[19px]'
            : 'h-5.5 w-5.5 bg-text data-[state=checked]:translate-x-[22px]',
        )}
      />
    </RS.Root>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton rounded-[var(--radius-card)]', className)} aria-hidden />;
}

export function Badge({
  children,
  tone = 'neutral',
  className,
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'pink' | 'success' | 'danger' | 'warning' | 'outline';
  className?: string;
  title?: string;
}) {
  const tones = {
    neutral: 'bg-white/8 text-text/85',
    pink: 'bg-pink/15 text-pink-soft',
    success: 'bg-success/15 text-success',
    danger: 'bg-danger/15 text-danger',
    warning: 'bg-warning/15 text-warning',
    outline: 'border border-white/25 text-text/85',
  };
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span
      role="status"
      aria-label={label}
      className={cn(
        'inline-block h-6 w-6 rounded-full border-2 border-pink/25 border-t-pink animate-spin',
        className,
      )}
    />
  );
}

export function ProgressBar({
  value,
  className,
  label,
}: {
  value: number;
  className?: string;
  label?: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      className={cn('h-1 w-full overflow-hidden rounded-full bg-white/15', className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={label ?? 'Progress'}
    >
      <div
        className="h-full rounded-full bg-pink transition-[width] duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-md border border-line bg-bg-2 px-1.5 py-0.5 font-sans text-[11px] text-muted">
      {children}
    </kbd>
  );
}
